import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Mic, RotateCcw } from "lucide-react";
import {
  formatFingerPatternLabel,
  formatPitchPatternLabel,
  resolvePitchLabel,
  type RhythmPathMission,
  type RhythmSubMission,
} from "../../../lib/rhythm/rhythmPathPlan";
import {
  buildStepExpectedHits,
  createVolumeOnsetTracker,
  evaluateRhythmSuccess,
  getActiveExpectedHitIndex,
  scoreRhythmSession,
  sessionDurationMs as calcSessionDurationMs,
  type DetectedAttack,
  type ExpectedHit,
} from "../../../lib/rhythm/rhythmEngine";
import { FingerIndicator } from "../FingerIndicator";
import { StrumGestureIndicator } from "../StrumGestureIndicator";
import { OpenStringsPanel } from "../OpenStringsPanel";
import { StringNumberBadge } from "../StringNumberBadge";
import { RhythmFigureGlyph } from "../RhythmFigureGlyph";
import { RhythmBeatTracker } from "../RhythmBeatTracker";
import { RhythmExerciseTimeline } from "../RhythmExerciseTimeline";
import { RhythmDurationLegend } from "../RhythmDurationLegend";
import { SubMissionStepper } from "../SubMissionStepper";
import { RhythmSubMissionMenu } from "../RhythmSubMissionMenu";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { GymCountdownOverlay } from "../../gym/GymCountdownOverlay";
import { SongLearnCanvasBackdrop } from "../../SongLearnCanvasBackdrop";
import { StudioTunerControl } from "../../StudioTunerControl";
import { useMetronome } from "../../../hooks/useMetronome";
import { usePitchDetector } from "../../../hooks/usePitchDetector";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import type { StrumDirection, StrumGesture } from "../../../lib/rhythm/rhythmPathPlan";

type Phase = "menu" | "intro" | "countdown" | "playing" | "stepResult";

export interface RhythmPathMissionStudioProps {
  mission: RhythmPathMission;
  wasCompletedBefore: boolean;
  completedSubMissionIds: string[];
  preview?: boolean;
  pathMode?: boolean;
  onBack: () => void;
  /** Al superar un paso (actualiza progreso de submisión). */
  onStepComplete: (subMissionId: string) => void;
  /** Todas las submisiones hechas → salir a la lista. */
  onMissionFullyComplete: () => void;
}

export function RhythmPathMissionStudio({
  mission,
  wasCompletedBefore,
  completedSubMissionIds,
  preview = false,
  pathMode = false,
  onBack,
  onStepComplete,
  onMissionFullyComplete,
}: RhythmPathMissionStudioProps) {
  const [phase, setPhase] = useState<Phase>("menu");
  const [stepIndex, setStepIndex] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [bpm, setBpmLocal] = useState(mission.bpm);
  const [hitCount, setHitCount] = useState(0);
  const [totalHits, setTotalHits] = useState(0);
  const [attacksHeard, setAttacksHeard] = useState(0);
  const [passed, setPassed] = useState(false);
  const [pitchMisses, setPitchMisses] = useState(0);
  const [restViolations, setRestViolations] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [countdownBeat, setCountdownBeat] = useState<number | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [liveVolume, setLiveVolume] = useState(0);

  const step: RhythmSubMission | undefined = mission.subMissions[stepIndex];
  const isLastStep = stepIndex >= mission.subMissions.length - 1;

  const {
    beat: metroBeat,
    start: startMetro,
    stop: stopMetro,
    setBpm: setMetroBpm,
  } = useMetronome();
  const { state: pitch, startListening, stopListening, getLiveVolume, getLivePitch } =
    usePitchDetector({
      threshold: 0.002,
      holdTimeMs: 0,
    });
  const getLiveVolumeRef = useRef(getLiveVolume);
  getLiveVolumeRef.current = getLiveVolume;
  const getLivePitchRef = useRef(getLivePitch);
  getLivePitchRef.current = getLivePitch;

  const sessionStartRef = useRef(0);
  const attacksRef = useRef<DetectedAttack[]>([]);
  const expectedRef = useRef<ExpectedHit[]>([]);
  const stepMetaRef = useRef({ toleranceMs: mission.toleranceMs, successRatio: mission.successRatio });
  const onsetRef = useRef(
    createVolumeOnsetTracker({ absFloor: 0.004, delta: 0.007, minGapMs: 80 })
  );
  const volumeRef = useRef(0);
  const phaseRef = useRef<Phase>("intro");
  const rafRef = useRef<number | null>(null);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countIvRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stepBuild = useMemo(() => {
    if (!step) return null;
    return buildStepExpectedHits(mission, step, bpm);
  }, [mission, step, bpm]);

  const expected = stepBuild?.expected ?? [];
  const playBpm = stepBuild?.bpm ?? bpm;
  const sessionLenMs = useMemo(
    () => calcSessionDurationMs(expected, playBpm),
    [expected, playBpm]
  );

  const activeHitIdx =
    phase === "playing" ? getActiveExpectedHitIndex(expected, elapsedMs) : -1;
  const activeHit = activeHitIdx >= 0 ? expected[activeHitIdx] : null;

  useEffect(() => {
    volumeRef.current = pitch.volume;
    if (phase === "countdown" || phase === "playing") {
      setLiveVolume(pitch.volume);
    }
  }, [pitch.volume, phase]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    setMetroBpm(playBpm);
  }, [playBpm, setMetroBpm]);

  useEffect(() => {
    setBpmLocal(mission.bpm);
    setStepIndex(0);
    setPhase("menu");
    setFailCount(0);
  }, [mission.id, mission.bpm]);

  const cleanup = useCallback(() => {
    stopMetro();
    stopListening();
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    if (countIvRef.current) {
      clearInterval(countIvRef.current);
      countIvRef.current = null;
    }
  }, [stopMetro, stopListening]);

  useEffect(() => () => cleanup(), [cleanup]);

  const finishSession = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    stopMetro();
    stopListening();

    const heard = attacksRef.current.length;
    setAttacksHeard(heard);
    const { toleranceMs, successRatio } = stepMetaRef.current;
    const scores = scoreRhythmSession(
      expectedRef.current,
      attacksRef.current,
      toleranceMs,
      mission.requirePitch
    );
    const result = evaluateRhythmSuccess(
      successRatio,
      expectedRef.current,
      scores,
      attacksRef.current,
      toleranceMs
    );
    setHitCount(result.hitCount);
    setTotalHits(result.total);
    setPassed(result.passed);
    setPitchMisses(result.pitchMisses);
    setRestViolations(result.restViolations);
    setPhase("stepResult");

    if (result.passed && step) {
      onStepComplete(step.id);
    }

    if (!result.passed) {
      setFailCount((c) => {
        const next = c + 1;
        if (mission.fallbackBpm && next >= 2 && bpm > mission.fallbackBpm) {
          setBpmLocal(mission.fallbackBpm);
        }
        return next;
      });
    }
  }, [bpm, mission, onStepComplete, step, stopListening, stopMetro]);

  const startPlaying = useCallback(async () => {
    if (!step) return;
    setMicError(null);
    cleanup();

    const built = buildStepExpectedHits(mission, step, bpm);
    expectedRef.current = built.expected;
    stepMetaRef.current = {
      toleranceMs: built.toleranceMs,
      successRatio: built.successRatio,
    };
    const durationMs = calcSessionDurationMs(built.expected, built.bpm);

    attacksRef.current = [];
    onsetRef.current.reset();
    setAttacksHeard(0);
    setElapsedMs(0);
    setLiveVolume(0);
    phaseRef.current = "countdown";
    setPhase("countdown");
    setCountdownBeat(1);

    try {
      await startListening();
    } catch {
      setMicError("No se pudo abrir el micrófono. Revisa permisos del navegador.");
      phaseRef.current = "intro";
      setPhase("intro");
      return;
    }

    setMetroBpm(built.bpm);
    startMetro();

    let beat = 1;
    const beatMs = 60_000 / built.bpm;
    countIvRef.current = setInterval(() => {
      beat += 1;
      if (beat > 4) {
        if (countIvRef.current) {
          clearInterval(countIvRef.current);
          countIvRef.current = null;
        }
        setCountdownBeat(null);
        phaseRef.current = "playing";
        setPhase("playing");
        // Reinicia el metrónomo al t=0 de la sesión (misma reloj que el dedo / tracker).
        stopMetro();
        sessionStartRef.current = performance.now();
        startMetro();
        onsetRef.current.reset();

        const tick = () => {
          if (phaseRef.current !== "playing") return;
          const t = performance.now() - sessionStartRef.current;
          const vol = getLiveVolumeRef.current();
          const live = getLivePitchRef.current();
          volumeRef.current = vol;
          setElapsedMs(t);
          setLiveVolume(vol);

          const attack = onsetRef.current.feed(vol, t);
          if (attack) {
            attacksRef.current.push({
              ...attack,
              note: live.note,
              octave: live.octave,
              midi: live.midi > 0 ? live.midi : undefined,
            });
            setAttacksHeard(attacksRef.current.length);
          }

          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);

        endTimerRef.current = setTimeout(() => {
          finishSession();
        }, durationMs + 250);
      } else {
        setCountdownBeat(beat);
      }
    }, beatMs);
  }, [
    bpm,
    cleanup,
    finishSession,
    mission,
    setMetroBpm,
    startListening,
    startMetro,
    stopMetro,
    step,
  ]);

  const goToMenu = useCallback(() => {
    cleanup();
    setFailCount(0);
    setPhase("menu");
  }, [cleanup]);

  const advanceOrComplete = useCallback(() => {
    if (passed && isLastStep) {
      onMissionFullyComplete();
      return;
    }
    goToMenu();
  }, [goToMenu, isLastStep, onMissionFullyComplete, passed]);

  const isStrum = mission.uiMode === "strum" || !mission.requirePitch;
  const patternLabel = step ? formatFingerPatternLabel(step) : "";
  const pitchLabel = step ? formatPitchPatternLabel(step) : "";
  const previewAttack = step?.targetPattern?.find((n) => n.type === "attack");
  const previewRest = step?.targetPattern?.find((n) => n.type === "rest");
  const previewToken =
    previewAttack?.finger ??
    step?.fingerPattern?.find((f) => f !== "rest") ??
    "i";
  const previewString: 1 | 2 | 3 | 4 | 5 | 6 =
    previewAttack?.pitch != null
      ? resolvePitchLabel(previewAttack.pitch).openString
      : (step?.stringPattern?.[0] ?? 1);
  const dimHero = phase === "intro" || phase === "countdown";
  const heroIsRest = phase === "playing" && Boolean(activeHit?.isRest);
  const heroFinger = heroIsRest
    ? "rest"
    : phase === "playing"
      ? (activeHit?.finger ?? null)
      : previewToken;
  const heroString = heroIsRest
    ? null
    : phase === "playing"
      ? (activeHit?.openString ?? null)
      : previewString;

  const heroGesture: StrumGesture | null = heroIsRest
    ? "mute"
    : phase === "playing"
      ? (activeHit?.gesture ?? (activeHit && !activeHit.isRest ? "hand" : null))
      : previewRest && !previewAttack
        ? "mute"
        : (previewAttack?.gesture ?? "hand");
  const heroDirection: StrumDirection | null = heroIsRest
    ? null
    : phase === "playing"
      ? (activeHit?.direction ?? "down")
      : (previewAttack?.direction ?? "down");

  // Beat UI desde el mismo reloj que el indicador de dedo (elapsedMs).
  const msPerBeat = 60_000 / Math.max(1, playBpm);
  const sessionBeats = phase === "playing" ? elapsedMs / msPerBeat : 0;
  const uiBeat =
    phase === "playing"
      ? Math.floor(sessionBeats % 4)
      : phase === "countdown" && countdownBeat != null
        ? (countdownBeat - 1) % 4
        : metroBeat;
  const uiBeatProgress = phase === "playing" ? sessionBeats % 1 : 0;

  const heroDurationBeats = heroIsRest
    ? (activeHit?.durationBeats ?? null)
    : phase === "playing" && activeHit && !activeHit.isRest
      ? activeHit.durationBeats
      : stepBuild?.expected[0] && !stepBuild.expected[0].isRest
        ? stepBuild.expected[0].durationBeats
        : null;

  const stepFeedbackOk = step?.feedbackOk ?? mission.feedbackOk;
  const stepFeedbackError = step?.feedbackError ?? mission.feedbackError;

  if (phase === "menu") {
    return (
      <SongLearnCanvasBackdrop ambient="lite" className="flex min-h-0 flex-1 flex-col w-full">
        <div
          className={`flex h-full min-h-0 flex-col ${
            pathMode ? "w-full max-w-[min(100%,1600px)] mx-auto" : "w-full"
          }`}
        >
          <header className="mx-2 mt-2 mb-1 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-3 backdrop-blur-sm sm:mx-4">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-white/5"
                style={{ borderColor: PATH.cardBorder, color: PATH.textMuted }}
                aria-label="Volver"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <p className="mb-0.5 text-[10px] uppercase tracking-[0.14em] text-[#99f6e4]/70">
                  {mission.eyebrow}
                </p>
                <h1 className="truncate font-serif text-sm font-light sm:text-base">
                  {mission.title}
                </h1>
              </div>
            </div>
            <StudioTunerControl compact />
          </header>
          <RhythmSubMissionMenu
            mission={mission}
            completedSubMissionIds={completedSubMissionIds}
            preview={preview}
            onSelectStep={(i) => {
              setStepIndex(i);
              setFailCount(0);
              setPhase("intro");
            }}
            onBack={onBack}
          />
        </div>
      </SongLearnCanvasBackdrop>
    );
  }

  if (!step) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-white/50">
        No hay pasos en esta misión.
      </div>
    );
  }

  return (
    <SongLearnCanvasBackdrop ambient="lite" className="flex min-h-0 flex-1 flex-col w-full">
      <div
        className={`flex h-full min-h-0 flex-col ${
          pathMode ? "w-full max-w-[min(100%,1600px)] mx-auto" : "w-full"
        }`}
      >
        <header className="mx-2 mt-2 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-3 backdrop-blur-sm sm:mx-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => {
                cleanup();
                setPhase("menu");
              }}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-white/5"
              style={{ borderColor: PATH.cardBorder, color: PATH.textMuted }}
              aria-label="Volver al menú de pasos"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              {pathMode && (
                <p className="mb-0.5 text-[10px] uppercase tracking-[0.14em] text-[#99f6e4]/70">
                  {mission.eyebrow}
                </p>
              )}
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h1 className="truncate font-serif text-sm font-light sm:text-base">
                  {mission.title}
                </h1>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#99f6e4]/75 tabular-nums">
                  {playBpm} BPM
                </span>
              </div>
              <p className="truncate text-[11px] text-white/45">
                {step.title}
                {step.subtitle ? ` · ${step.subtitle}` : ""}
                {patternLabel ? ` · ${patternLabel}` : ""}
                {pitchLabel ? ` · ${pitchLabel}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <StudioTunerControl compact />
            <SubMissionStepper
              current={stepIndex}
              total={mission.subMissions.length}
              labels={mission.subMissions.map((s) => s.title)}
              kind={step.kind ?? null}
            />
          </div>
        </header>

        <div className="relative mx-2 mb-2 mt-2 flex min-h-[min(58vh,520px)] flex-1 flex-col overflow-hidden rounded-2xl sm:mx-4">
          <div
            className={`flex min-h-0 flex-1 flex-col transition-opacity duration-500 ${
              dimHero ? "opacity-35" : "opacity-100"
            }`}
          >
            {/* Hero: leyenda | cuerdas | dedo+figura (compactos) */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-3 py-2 sm:px-4">
                <div className="flex w-full max-w-5xl flex-col items-center justify-center gap-5 lg:flex-row lg:items-center lg:gap-8">
                  <div className="flex shrink-0 items-start gap-3">
                    <RhythmDurationLegend
                      expected={expected}
                      activeBeats={heroDurationBeats}
                      isRestActive={heroIsRest}
                      className="hidden sm:block w-[7.5rem]"
                    />
                    <OpenStringsPanel
                      activeString={
                        isStrum || !mission.requirePitch ? null : heroString
                      }
                      activeStrings={
                        isStrum && !heroIsRest
                          ? ([6, 5, 4, 3, 2, 1] as const)
                          : null
                      }
                      intensity={
                        heroIsRest
                          ? 0
                          : phase === "playing"
                            ? liveVolume
                            : isStrum || heroString
                              ? 0.45
                              : 0
                      }
                    />
                  </div>

                  {/* Dedo (fingers) o rasgueo (strum) + figura */}
                  <div className="flex flex-col items-center gap-1.5 sm:gap-2">
                    {isStrum ? (
                      <StrumGestureIndicator
                        gesture={
                          phase === "playing" || phase === "countdown"
                            ? heroGesture
                            : (previewAttack?.gesture ?? "hand")
                        }
                        direction={heroDirection}
                        size="lg"
                        showSnap={false}
                      />
                    ) : (
                      <FingerIndicator active={heroFinger} size="lg" />
                    )}
                    <div className="flex items-center gap-3">
                      {!heroIsRest &&
                        !isStrum &&
                        mission.requirePitch &&
                        heroString != null && (
                          <StringNumberBadge
                            openString={heroString}
                            active
                            size="lg"
                          />
                        )}
                      {!heroIsRest && isStrum && (
                        <span className="rounded-xl border border-[#99f6e4]/40 bg-[#99f6e4]/10 px-2.5 py-1.5 text-[11px] font-medium tracking-wide text-[#99f6e4]">
                          {heroDirection === "up" ? "↑ 6 cuerdas" : "↓ 6 cuerdas"}
                        </span>
                      )}
                      <RhythmFigureGlyph
                        durationBeats={heroDurationBeats}
                        isRest={heroIsRest}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {(phase === "playing" || phase === "countdown") && (
                <div
                  className={`flex w-full shrink-0 flex-col gap-2 border-t border-white/[0.05] px-2 py-2 sm:px-3 ${
                    isStrum ? "py-3" : ""
                  }`}
                >
                  <RhythmBeatTracker
                    beat={uiBeat}
                    beatProgress={uiBeatProgress}
                    strumMode={isStrum}
                    expected={expected}
                    sessionBeats={sessionBeats}
                  />
                  {phase === "playing" && (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-white/45">
                        <Mic
                          className={`h-3.5 w-3.5 ${
                            liveVolume > 0.01 ? "text-[#99f6e4] animate-pulse" : ""
                          }`}
                        />
                        Mic
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full rounded-full bg-[#99f6e4] transition-[width] duration-75"
                          style={{
                            width: `${Math.min(100, liveVolume * 220)}%`,
                          }}
                        />
                      </div>
                      <span className="tabular-nums text-[11px] text-white/40">
                        {attacksHeard}/{expected.filter((e) => !e.isRest).length}
                      </span>
                    </div>
                  )}
                  {micError && (
                    <p className="text-center text-[11px] text-red-300">{micError}</p>
                  )}
                </div>
              )}
            </div>

            {expected.length > 0 && (
              <div className="w-full shrink-0 border-t border-white/[0.06] px-1 pb-2 pt-1 sm:px-2">
                <RhythmExerciseTimeline
                  expected={expected}
                  activeIndex={
                    phase === "playing"
                      ? activeHitIdx
                      : phase === "intro" || phase === "countdown"
                        ? 0
                        : -1
                  }
                  strumMode={isStrum}
                />
              </div>
            )}
          </div>

          {phase === "intro" && (
            <ModuleIntroModal
              title={step.title}
              subtitle={step.subtitle ?? ""}
              description={step.goal ?? ""}
              instructions={
                step.instructions?.length
                  ? step.instructions
                  : [
                      ...(step.goal ? [step.goal] : mission.instructions),
                      mission.professorTip,
                    ]
              }
              tip={step.tip ?? mission.professorTip}
              tags={[
                isStrum ? "↓↑ rasgueo" : "i–m",
                `${expected.filter((e) => !e.isRest).length} golpes`,
                ...(isStrum &&
                expected.some((e) => e.gesture === "mute" || e.isRest)
                  ? ["muteo"]
                  : []),
                ...(step.kind === "resistencia" || step.kind === "examen"
                  ? [step.kind === "examen" ? "Examen" : "Resistencia"]
                  : []),
              ]}
              bpm={playBpm}
              stepProgress={{
                current: stepIndex + 1,
                total: mission.subMissions.length,
              }}
              timeEstimateMin={
                step.kind === "resistencia" || step.kind === "examen"
                  ? Math.max(2, Math.round(sessionLenMs / 60_000) + 1)
                  : Math.min(4, mission.timeEstimateMin)
              }
              accent="#99f6e4"
              accentSoft="rgba(153, 246, 228, 0.12)"
              accentGlow="rgba(153, 246, 228, 0.22)"
              borderHover={PATH.cardBorderMint}
              textMain={PATH.text}
              textMuted={PATH.textMuted}
              textDim={PATH.textDim}
              wasCompletedBefore={wasCompletedBefore && stepIndex === 0}
              beginLabel="Empezar · cuenta 1-2-3-4"
              onBegin={() => void startPlaying()}
            />
          )}

          {phase === "countdown" && countdownBeat != null && (
            <GymCountdownOverlay phase={countdownBeat} />
          )}

          {phase === "stepResult" && (
            <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/62" aria-hidden />
              <div
                className="relative z-10 w-full max-w-md rounded-2xl border px-6 py-7 text-center"
                style={{
                  background:
                    "linear-gradient(165deg, rgba(42,38,36,0.97) 0%, rgba(28,26,25,0.98) 100%)",
                  borderColor: PATH.cardBorderMint,
                  boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
                }}
              >
                <p className="mb-3 text-4xl">{passed ? (isLastStep ? "🌟" : "✨") : "🔄"}</p>
                <h2 className="mb-1 font-serif text-xl text-white/90">
                  {passed
                    ? isLastStep
                      ? "Misión superada"
                      : "Paso superado"
                    : "Aún no"}
                </h2>
                <p className="mb-2 text-[11px] uppercase tracking-[0.14em] text-[#99f6e4]/70">
                  {step.title} · {stepIndex + 1}/{mission.subMissions.length}
                </p>
                <p className="mb-2 text-sm text-white/60">
                  {passed
                    ? isLastStep
                      ? mission.feedbackOk
                      : stepFeedbackOk
                    : restViolations > 0
                      ? "Tocaste durante un silencio. Guarda el shh y reintenta este paso."
                      : attacksHeard === 0
                        ? "No se oyeron golpes. Acerca la cuerda al mic y usa auriculares."
                        : stepFeedbackError}
                </p>
                <p className="mb-5 text-xs tabular-nums text-white/40">
                  A tiempo: {hitCount}/{totalHits} · Detectados: {attacksHeard}
                  {mission.requirePitch && pitchMisses > 0
                    ? ` · Pitch erróneo: ${pitchMisses}`
                    : ""}
                  {restViolations > 0 ? ` · Silencio roto: ${restViolations}` : ""}
                </p>
                {failCount >= 2 &&
                  mission.fallbackBpm &&
                  bpm === mission.fallbackBpm && (
                    <p className="mb-4 text-[11px] text-amber-200/80">
                      Tempo bajado a {mission.fallbackBpm} BPM en este paso.
                    </p>
                  )}
                <div className="flex flex-col gap-2 sm:flex-row">
                  {passed ? (
                    <button
                      type="button"
                      onClick={advanceOrComplete}
                      className="flex-1 rounded-xl py-3 text-sm font-semibold text-[#100f0e]"
                      style={{ background: PATH.btnMint }}
                    >
                      {passed && isLastStep
                        ? "Misión lista · Volver"
                        : "Volver al menú de pasos"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void startPlaying()}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 py-3 text-sm font-semibold text-white/85 hover:bg-white/5"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Reintentar este paso
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={goToMenu}
                    className="flex-1 rounded-xl py-3 text-sm text-white/50 hover:text-white/80"
                  >
                    Menú de pasos
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SongLearnCanvasBackdrop>
  );
}
